import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, MessageCircle, ShieldCheck, Users, Clapperboard, Check, CheckCircle2,
  Heart, Megaphone, Navigation, Building2, Film, Mountain, Send, Loader2, Sparkles,
} from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { WhatsAppFloat } from "@/components/cine/WhatsAppFloat";
import { EQUIPMENT } from "@/constants/aero";
import { MountainVideo } from "@/components/aero/MountainVideo";
import { SOCIALS } from "@/constants/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const PROJECTS = [
  { id: "Wedding Film", icon: Heart },
  { id: "Brand Commercial", icon: Megaphone },
  { id: "Drone Shoot", icon: Navigation },
  { id: "Real Estate", icon: Building2 },
  { id: "Documentary", icon: Film },
  { id: "Travel Film", icon: Mountain },
];

const DRONE_META = {
  "DJI Inspire 3": { display: "DJI Inspire 3", tag: "Hollywood-grade cinema platform", best: "Best for Feature Films & Premium Commercials" },
  "DJI Mavic 4 Pro Creator Combo": { display: "DJI Mavic 4 Pro", tag: "Next-generation aerial imaging", best: "Best for Commercials & Travel" },
  "DJI Mavic 3 Cine": { display: "DJI Mavic 3 Cine", tag: "Professional aerial filmmaking", best: "Best for Weddings & Brand Films" },
  "DJI Mavic 3 Classic": { display: "DJI Mavic 3 Classic", tag: "Reliable cinematic aerials", best: "Best for General Productions" },
  "DJI Avata 2 (FPV)": { display: "DJI Avata 2", tag: "Dynamic FPV action shots", best: "Best for Action & Fast Motion" },
};

const DRONES = EQUIPMENT.map((e) => ({ ...e, ...DRONE_META[e.name] }));

const RECOMMEND = {
  "Wedding Film": "DJI Mavic 3 Cine",
  "Brand Commercial": "DJI Inspire 3",
  "Drone Shoot": "DJI Mavic 3 Classic",
  "Real Estate": "DJI Mavic 3 Classic",
  "Documentary": "DJI Mavic 4 Pro Creator Combo",
  "Travel Film": "DJI Mavic 4 Pro Creator Combo",
};

const BUDGETS = ["Under ₹50K", "₹50K — ₹1L", "₹1L — ₹3L", "₹3L+", "Not sure yet"];

const TRUST = [
  { icon: ShieldCheck, title: "Professional Gear", sub: "Cinema-grade equipment" },
  { icon: Users, title: "Experienced Team", sub: "9+ Years Experience" },
  { icon: Clapperboard, title: "Trusted Productions", sub: "Bollywood • OTT • Government" },
];

const TRUST_STATS = ["9+|Years", "26+|Productions", "Bollywood|Feature Films", "OTT|Series", "Government|Projects"];

const inputCls =
  "w-full bg-[#F8F6F2] border border-[#E5E7EB] focus:border-[#4F6D8A] focus:shadow-[0_0_0_4px_rgba(79,109,138,0.12)] outline-none rounded-2xl px-5 py-4 text-sm text-[#111827] placeholder:text-[#9CA3AF] transition-all duration-300";

const StepLabel = ({ n, title, sub }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease }}
    className="max-w-2xl"
  >
    <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">Step {n}</p>
    <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-[1.06] text-[#111827]">{title}</h2>
    {sub && <p className="mt-4 text-[#6B7280] leading-relaxed">{sub}</p>}
    <span className="mt-5 block w-14 h-[2px] bg-[#4F6D8A]" />
  </motion.div>
);

export default function BookShootPage() {
  const [project, setProject] = useState("");
  const [drone, setDrone] = useState("");
  const [waHref, setWaHref] = useState(SOCIALS.instagram);
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", location: "", budget: BUDGETS[4], brief: "" });
  const [sending, setSending] = useState(false);
  const droneRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data.whatsapp_number) {
          const msg = encodeURIComponent(
            `Hi Akhilesh, I want to book a ${project || "shoot"}${drone ? ` using ${drone}` : ""}${form.date ? ` on ${form.date}` : ""}.`
          );
          setWaHref(`https://wa.me/${r.data.whatsapp_number}?text=${msg}`);
        }
      })
      .catch(() => {});
  }, [project, drone, form.date]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const recommend = () => {
    if (!project) {
      toast.error("Pehle Step 1 mein project type chuno");
      return;
    }
    const rec = RECOMMEND[project];
    setDrone(rec);
    toast.success(`${DRONE_META[rec].display} recommended for ${project}`);
    droneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async () => {
    if (form.name.trim().length < 2 || form.phone.trim().length < 10 || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Naam, phone aur valid email bharna zaroori hai");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/shoot-enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        project_type: project || "Not selected",
        date: form.date,
        budget: form.budget,
        location: form.location.trim(),
        brief: `${form.brief.trim()}${drone ? ` | Preferred Drone: ${drone}` : ""}`.trim() || "—",
      });
      toast.success("Booking request bhej di gayi! 24 hours ke andar response milega.");
      setForm({ name: "", phone: "", email: "", date: "", location: "", budget: BUDGETS[4], brief: "" });
    } catch {
      toast.error("Kuch gadbad ho gayi — WhatsApp par message kar do.");
    } finally {
      setSending(false);
    }
  };

  const selectCls = (active) =>
    `group relative bg-white rounded-[22px] border p-6 text-left transition-all duration-300 hover:-translate-y-1.5 ${
      active
        ? "border-[#0F2A43] shadow-[0_25px_60px_-20px_rgba(15,42,67,0.45)] ring-1 ring-[#0F2A43]"
        : "border-[#E5E7EB] shadow-[0_10px_35px_-15px_rgba(15,42,67,0.15)] hover:shadow-[0_30px_60px_-20px_rgba(15,42,67,0.3)]"
    }`;

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-[#F8F6F2] text-[#111827] overflow-x-clip"
      data-testid="book-shoot-page"
    >
      <AeroNav />

      {/* Hero — 21:9 cinematic banner */}
      <section className="relative overflow-hidden" data-testid="book-hero">
        <div className="relative aspect-[21/9] min-h-[340px]">
          <MountainVideo testid="book-hero-mountain-video" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F8F6F2] via-[#F8F6F2]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F8F6F2] via-transparent to-white/40" />
          <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10 h-full flex items-center pt-[80px]">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease }} className="max-w-2xl">
              <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A] flex items-center gap-4">
                <span className="w-10 h-px bg-[#4F6D8A]/60 inline-block" /> Book a Shoot
              </p>
              <h1 className="mt-4 font-serif text-[36px] md:text-[60px] leading-[1.04] text-[#111827]">
                Book Your Cinematic Production
              </h1>
              <p className="mt-4 text-[#374151] leading-relaxed max-w-lg">
                Choose your project, select the perfect drone, and let's create unforgettable visuals.
              </p>
            </motion.div>
          </div>
        </div>
        {/* Trust icons */}
        <div className="max-w-[1320px] mx-auto px-6 md:px-10 -mt-10 relative z-20 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TRUST.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.25 + i * 0.08, ease }}
                  data-testid={`book-trust-${i}`}
                  className="bg-white rounded-[18px] border border-[#E5E7EB] shadow-[0_15px_40px_-18px_rgba(15,42,67,0.25)] px-6 py-5 flex items-center gap-4"
                >
                  <span className="w-11 h-11 rounded-full bg-[#4F6D8A]/10 flex items-center justify-center shrink-0">
                    <Icon size={18} strokeWidth={1.4} className="text-[#0F2A43]" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-sm text-[#111827]">{t.title}</p>
                    <p className="mt-0.5 font-mono text-[8px] tracking-[0.2em] uppercase text-[#6B7280]">{t.sub}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step 1 — Project */}
      <section className="py-[90px] md:py-[100px]" data-testid="book-step-1">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <StepLabel n="01" title="Select Your Project" />
          <div className="mt-12 grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {PROJECTS.map((p, i) => {
                const Icon = p.icon;
                const active = project === p.id;
                return (
                  <motion.button
                    key={p.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-30px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.07, ease }}
                    onClick={() => setProject(p.id)}
                    data-testid={`book-project-${p.id.toLowerCase().replace(/\s+/g, "-")}`}
                    className={selectCls(active)}
                  >
                    {active && (
                      <span className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#0F2A43] flex items-center justify-center">
                        <Check size={12} strokeWidth={3} className="text-white" />
                      </span>
                    )}
                    <Icon size={24} strokeWidth={1.3} className={active ? "text-[#0F2A43]" : "text-[#4F6D8A]"} />
                    <p className="mt-5 font-display font-bold text-lg text-[#111827]">{p.id}</p>
                    <p className="mt-1.5 font-mono text-[8px] tracking-[0.22em] uppercase text-[#6B7280]">Cinematic Production</p>
                  </motion.button>
                );
              })}
            </div>

            {/* Recommendation card */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.6, delay: 0.1, ease }}
              className="lg:col-span-4 bg-[#0F2A43] rounded-[22px] p-8 md:p-9 shadow-[0_30px_70px_-25px_rgba(15,42,67,0.55)] lg:sticky lg:top-28"
              data-testid="book-recommend-card"
            >
              <span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles size={18} strokeWidth={1.4} className="text-white" />
              </span>
              <p className="mt-5 font-serif text-2xl md:text-[28px] text-white leading-snug">Not Sure Which Drone?</p>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">
                Get an instant recommendation based on your project type.
              </p>
              <button
                data-testid="book-recommend-btn"
                onClick={recommend}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-white text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-6 py-4 rounded-full hover:bg-[#8FAECC] hover:text-white hover:-translate-y-0.5 transition-all duration-300"
              >
                Get Recommendation <ArrowRight size={13} />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Step 2 — Drone */}
      <section className="py-[90px] md:py-[100px] bg-white border-y border-[#E5E7EB]" data-testid="book-step-2" ref={droneRef}>
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <StepLabel n="02" title="Which Drone Would You Like for Your Shoot?" sub="Choose the aircraft that best matches your cinematic vision." />
          <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {DRONES.map((d, i) => {
              const active = drone === d.name;
              return (
                <motion.button
                  key={d.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.07, ease }}
                  onClick={() => setDrone(d.name)}
                  data-testid={`book-drone-${i}`}
                  className={`${selectCls(active)} overflow-hidden p-0 ${d.hero ? "sm:col-span-2 xl:col-span-1" : ""}`}
                >
                  <div className="relative h-44 overflow-hidden bg-[#F8F6F2]">
                    <img src={d.img} alt={d.display} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    {active && (
                      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#0F2A43] text-white font-mono text-[8px] font-bold tracking-[0.25em] uppercase px-3.5 py-1.5 rounded-full">
                        <CheckCircle2 size={11} /> Selected
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    {active && (
                      <span className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#0F2A43] flex items-center justify-center">
                        <Check size={12} strokeWidth={3} className="text-white" />
                      </span>
                    )}
                    <p className="font-display font-bold text-lg text-[#111827]">{d.display}</p>
                    <p className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-[#4F6D8A]">{d.tag}</p>
                    <ul className="mt-4 space-y-2">
                      {d.highlights.slice(0, 3).map((h) => (
                        <li key={h} className="flex items-center gap-2.5 text-[13px] text-[#6B7280]">
                          <Check size={12} strokeWidth={2} className="text-[#4F6D8A] shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-5 pt-4 border-t border-[#E5E7EB] font-mono text-[8px] tracking-[0.2em] uppercase text-[#0F2A43] font-bold">
                      {d.best}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step 3 — Details */}
      <section className="py-[90px] md:py-[100px]" data-testid="book-step-3" ref={formRef}>
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <StepLabel n="03" title="Tell Us About Your Shoot" />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease }}
            className="mt-10 bg-white border border-[#E5E7EB] rounded-[22px] p-7 md:p-10 shadow-[0_25px_70px_-25px_rgba(15,42,67,0.25)]"
            data-testid="book-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="book-form-name" value={form.name} onChange={set("name")} placeholder="Full Name" className={inputCls} />
              <input data-testid="book-form-phone" value={form.phone} onChange={set("phone")} placeholder="Phone Number" className={inputCls} />
              <input data-testid="book-form-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
              <input data-testid="book-form-date" type="date" value={form.date} onChange={set("date")} min={new Date().toISOString().split("T")[0]} className={`${inputCls} cursor-pointer`} />
              <input data-testid="book-form-location" value={form.location} onChange={set("location")} placeholder="Location (city / venue)" className={inputCls} />
              <select data-testid="book-form-budget" value={form.budget} onChange={set("budget")} className={`${inputCls} cursor-pointer`}>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <div className={`${inputCls} sm:col-span-2 flex flex-wrap gap-x-8 gap-y-2 !bg-white`}>
                <p className="text-sm text-[#6B7280]">
                  Project Type — <span className="text-[#0F2A43] font-medium" data-testid="book-form-project">{project || "Not selected (Step 1)"}</span>
                </p>
                <p className="text-sm text-[#6B7280]">
                  Preferred Drone — <span className="text-[#0F2A43] font-medium" data-testid="book-form-drone">{drone ? DRONE_META[drone].display : "Not selected (Step 2)"}</span>
                </p>
              </div>
            </div>
            <textarea
              data-testid="book-form-brief"
              value={form.brief}
              onChange={set("brief")}
              placeholder="Project details — vision, references, duration, anything specific..."
              rows={4}
              className={`${inputCls} mt-4 resize-none`}
            />
            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <button
                data-testid="book-form-submit"
                onClick={submit}
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full shadow-[0_15px_35px_-12px_rgba(15,42,67,0.5)] hover:bg-[#16395C] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />} Send Booking Request
              </button>
              <a
                data-testid="book-form-whatsapp"
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white border border-[#0F2A43]/25 text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#EEF2F6] hover:border-[#0F2A43] hover:-translate-y-0.5 transition-all duration-300"
              >
                <MessageCircle size={13} /> Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="pb-[100px] md:pb-[120px]" data-testid="book-trust-strip">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {TRUST_STATS.map((s, i) => {
              const [v, l] = s.split("|");
              return (
                <motion.div
                  key={s}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease }}
                  className="bg-white rounded-[18px] border border-[#E5E7EB] px-5 py-7 text-center shadow-[0_10px_30px_-15px_rgba(15,42,67,0.15)]"
                >
                  <p className="font-display font-extrabold text-2xl text-[#0F2A43]">{v}</p>
                  <p className="mt-1.5 font-mono text-[8px] tracking-[0.25em] uppercase text-[#6B7280]">{l}</p>
                </motion.div>
              );
            })}
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-14 text-center font-serif italic text-2xl md:text-3xl text-[#374151]"
          >
            "Professional equipment. Unforgettable results."
          </motion.p>
        </div>
      </section>

      <AeroFooter />
      <WhatsAppFloat />
    </motion.main>
  );
}
